import Header from '@/components/Header';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import { MapContainer, TileLayer, CircleMarker, Polyline, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Waypoints, Plus, Trash2, Pencil, X, Wand2, AlertTriangle, Clock, Ruler, Phone, CheckCircle2, RotateCcw, FileText, ShieldAlert, MapPin, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const DIRECTIONS = [
  { v: 'northbound', l: 'Northbound' }, { v: 'southbound', l: 'Southbound' },
  { v: 'eastbound', l: 'Eastbound' }, { v: 'westbound', l: 'Westbound' }, { v: 'other', l: 'Other / loop' },
];

const EMPTY = {
  title: '', origin: '', destination: '', date: new Date().toISOString().slice(0, 10),
  direction: 'northbound', highway_keys: [], waypoints: [],
  width_m: '', height_m: '', length_m: '', weight_t: '', pilot_phone: '', permit_no: '', notes: '',
  origin_lat: null, origin_lng: null, dest_lat: null, dest_lng: null, geometry: null,
};

const shortKey = (k) => `${k.replace('N-', '').replace('S-', '')} · ${k[0] === 'N' ? 'N' : 'S'}`;

function HazardPanel({ hazards, context }) {
  if (!hazards || !hazards.length) return null;
  const cap = context === 'editor' ? 12 : 6;
  const fails = hazards.filter((h) => h.level === 'fail').length;
  const warns = hazards.filter((h) => h.level === 'warn').length;
  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-orange-800 mb-1.5 flex items-center gap-1.5 flex-wrap">
        <AlertTriangle className="w-3.5 h-3.5" /> Route hazards <span className="font-normal normal-case text-orange-600/70">· live NZTA</span>
        {fails > 0 && <span className="chip text-[10px] bg-red-100 text-red-700 border border-red-200">{fails} critical</span>}
        {warns > 0 && <span className="chip text-[10px] bg-amber-100 text-amber-800 border border-amber-200">{warns} review</span>}
      </div>
      <div className="space-y-1.5">
        {hazards.slice(0, cap).map((h, i) => (
          <div key={i} data-testid={`hazard-item-${i}`}
            className={`text-xs rounded-md border px-2 py-1.5 ${h.level === 'fail' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
            <div className="font-semibold flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: h.level === 'fail' ? '#E11D48' : '#F59E0B' }} />
              {h.label}<span className="font-normal text-[10px] opacity-70">· {h.highway || 'NZ'}</span>
            </div>
            <div className="opacity-90">{h.location_area}{h.event_type ? ` — ${h.event_type}` : ''}</div>
            {h.fit && (
              <div className={`mt-1 inline-block text-[11px] font-bold px-1.5 py-0.5 rounded ${(h.fit.includes('NOT fit') || h.fit.includes('OVER')) ? 'bg-red-600 text-white' : h.fit.startsWith('Tight') ? 'bg-amber-500 text-white' : 'bg-teal-600 text-white'}`}>
                {h.fit}
              </div>
            )}
            {h.description && <div className="opacity-70 mt-0.5 line-clamp-2">{h.description}</div>}
          </div>
        ))}
        {hazards.length > cap && <div className="text-[10px] text-zinc-400">+{hazards.length - cap} more on this route</div>}
      </div>
      <p className="text-[10px] text-zinc-500 mt-2">Auto-detected from live NZTA events on your corridor (width/lane, slips &amp; washouts, weight, over-dimension). NZTA wording may not state exact dimensions — review each before you travel.</p>
    </div>
  );
}

export default function Loads() {
  const { user } = useAuth();
  const canCreate = user && (user.role === 'admin' || (user.is_load_pilot && user.pilot_status === 'approved'));
  const pilotPending = user && user.is_load_pilot && user.pilot_status === 'pending';
  const pilotRejected = user && user.is_load_pilot && user.pilot_status === 'rejected';
  const [loads, setLoads] = useState([]);
  const [tab, setTab] = useState('upcoming'); // upcoming | mine | completed
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);

  const load = (t = tab) => {
    const p = t === 'mine' ? { mine: true } : t === 'completed' ? { completed: true } : {};
    api.get('/loads', { params: p }).then((r) => setLoads(r.data)).catch(() => {});
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const startNew = () => { setForm(EMPTY); setEditing('new'); };
  const startEdit = (l) => {
    setForm({
      title: l.title, origin: l.origin, destination: l.destination, date: l.date, direction: l.direction,
      highway_keys: [...(l.highway_keys || [])],
      waypoints: (l.waypoints && l.waypoints.length ? l.waypoints.map((w) => ({ name: w.name, eta: w.eta || '', lat: w.lat ?? null, lng: w.lng ?? null })) : []),
      width_m: l.width_m ?? '', height_m: l.height_m ?? '', length_m: l.length_m ?? '', weight_t: l.weight_t ?? '',
      pilot_phone: l.pilot_phone || '', permit_no: l.permit_no || '', notes: l.notes || '',
      origin_lat: l.origin_lat ?? null, origin_lng: l.origin_lng ?? null, dest_lat: l.dest_lat ?? null, dest_lng: l.dest_lng ?? null,
      geometry: l.geometry || null,
    });
    setEditing(l.id);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const autoCorridor = async () => {
    if (!form.origin.trim() || !form.destination.trim()) { toast.error('Enter origin and destination first'); return; }
    setResolving(true);
    try {
      const { data } = await api.post('/routes/resolve-corridor', { start: form.origin.trim(), destination: form.destination.trim() });
      setForm((f) => ({
        ...f, highway_keys: data.highway_keys, title: f.title || `${f.origin} → ${f.destination}`,
        origin_lat: data.start.lat, origin_lng: data.start.lng, dest_lat: data.destination.lat, dest_lng: data.destination.lng,
        geometry: data.geometry || null,
      }));
      toast.success(`Corridor: ${data.highway_keys.length} highway(s) · ${data.distance_km} km — now click the map to add waypoints`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build corridor');
    } finally { setResolving(false); }
  };

  const setWp = (i, field, val) => setForm((f) => ({ ...f, waypoints: f.waypoints.map((w, j) => (j === i ? { ...w, [field]: val } : w)) }));
  const addWp = () => setForm((f) => ({ ...f, waypoints: [...f.waypoints, { name: '', eta: '' }] }));
  const rmWp = (i) => setForm((f) => ({ ...f, waypoints: f.waypoints.filter((_, j) => j !== i) }));

  const [editorRes, setEditorRes] = useState([]);
  useEffect(() => {
    const keys = form.highway_keys || [];
    if (editing === null || !keys.length) { setEditorRes([]); return; }
    Promise.all(keys.slice(0, 12).map((k) => api.get('/restrictions', { params: { highway_key: k } }).then((r) => r.data).catch(() => [])))
      .then((arr) => setEditorRes(arr.flat())).catch(() => setEditorRes([]));
    // eslint-disable-next-line
  }, [editing, JSON.stringify(form.highway_keys)]);

  const [editorHazards, setEditorHazards] = useState([]);
  useEffect(() => {
    const keys = form.highway_keys || [];
    if (editing === null || !keys.length) { setEditorHazards([]); return; }
    api.post('/loads/hazards-preview', { highway_keys: keys, geometry: form.geometry || null, width_m: form.width_m ? Number(form.width_m) : null, height_m: form.height_m ? Number(form.height_m) : null, weight_t: form.weight_t ? Number(form.weight_t) : null })
      .then((r) => setEditorHazards(r.data.hazards || [])).catch(() => setEditorHazards([]));
    // eslint-disable-next-line
  }, [editing, JSON.stringify(form.highway_keys), JSON.stringify(form.geometry), form.width_m, form.height_m, form.weight_t]);

  const onAddPin = async (lat, lng) => {
    const idx = form.waypoints.length;
    setForm((f) => ({ ...f, waypoints: [...f.waypoints, { name: 'Locating…', eta: '', lat, lng }] }));
    try {
      const { data } = await api.post('/geo/reverse', { lat, lng });
      setForm((f) => ({ ...f, waypoints: f.waypoints.map((w, j) => (j === idx ? { ...w, name: data.label } : w)) }));
    } catch {
      setForm((f) => ({ ...f, waypoints: f.waypoints.map((w, j) => (j === idx ? { ...w, name: `${lat.toFixed(3)}, ${lng.toFixed(3)}` } : w)) }));
    }
  };
  const onMovePin = (i, lat, lng) => setForm((f) => ({ ...f, waypoints: f.waypoints.map((w, j) => (j === i ? { ...w, lat, lng } : w)) }));

  const save = async () => {
    if (!form.title.trim() || !form.origin.trim() || !form.destination.trim()) { toast.error('Title, origin and destination are required'); return; }
    if (!form.highway_keys.length) { toast.error('Build the corridor to attach highways'); return; }
    setSaving(true);
    const num = (v) => (v === '' || v === null ? null : Number(v));
    const payload = {
      ...form, width_m: num(form.width_m), height_m: num(form.height_m), length_m: num(form.length_m), weight_t: num(form.weight_t),
      waypoints: form.waypoints.filter((w) => w.name.trim()).map((w) => ({ name: w.name.trim(), eta: w.eta || null, lat: w.lat ?? null, lng: w.lng ?? null })),
    };
    try {
      if (editing === 'new') await api.post('/loads', payload);
      else await api.patch(`/loads/${editing}`, payload);
      toast.success(editing === 'new' ? 'Load scheduled — subscribers on the corridor notified' : 'Load updated');
      cancel(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save load');
    } finally { setSaving(false); }
  };

  const act = async (id, verb) => {
    try { await api.post(`/loads/${id}/${verb}`); toast.success(verb === 'complete' ? 'Move completed & archived' : 'Load reopened'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this planned load?')) return;
    try { await api.delete(`/loads/${id}`); toast.success('Load deleted'); load(); } catch { toast.error('Could not delete'); }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="loads-page">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h1 className="font-display font-black text-3xl flex items-center gap-2"><Waypoints className="w-7 h-7 text-orange-500" /> Oversize Load Planner</h1>
            <p className="text-zinc-600 mt-1 text-sm max-w-2xl">Pilots publish planned oversize loads with waypoint ETAs so crews coordinate meets — and transport teams see delays coming. Conflicts, freight restrictions and corridor alerts are surfaced automatically.</p>
          </div>
          {canCreate && editing === null && (
            <button onClick={startNew} className="btn btn-primary shrink-0" data-testid="load-new-btn"><Plus className="w-4 h-4" /> Schedule a load</button>
          )}
        </div>

        <div className="flex gap-1 mb-6 border-b border-zinc-200">
          {[['upcoming', 'Upcoming'], ['mine', 'My loads'], ['completed', 'Completed']].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`loads-tab-${v}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === v ? 'border-orange-500 text-orange-600' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}>{l}</button>
          ))}
        </div>

        {!canCreate && (
          <div className="card mb-6 border-orange-200 bg-orange-50" data-testid="loads-plan-notice">
            {pilotPending ? (
              <p className="text-sm text-zinc-700" data-testid="loads-pending-notice">⏳ Your Load Pilot account is <strong>awaiting admin approval</strong>. Once approved, you'll be able to schedule loads and see contact details for other registered pilots. We'll email you when it's confirmed.</p>
            ) : pilotRejected ? (
              <p className="text-sm text-zinc-700">Your Load Pilot application was not approved. If you think this is a mistake, please contact <a href="mailto:loads@pttnz.net" className="text-orange-700 underline">loads@pttnz.net</a>.</p>
            ) : (
              <p className="text-sm text-zinc-700">You can view planned loads. To <strong>schedule</strong> your own oversize loads (free for Load Pilots), turn on <strong>"I'm a Load Pilot"</strong> in <Link to="/settings" className="text-orange-700 underline">Settings</Link>. New pilots are reviewed by an admin.</p>
            )}
          </div>
        )}

        {editing !== null && (
          <div className="card mb-8" data-testid="load-editor">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg">{editing === 'new' ? 'Schedule a planned load' : 'Edit planned load'}</h2>
              <button onClick={cancel} className="p-1.5 rounded-lg hover:bg-zinc-100" data-testid="load-cancel"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div><label className="label">Load title</label><input className="input" placeholder="e.g. Transformer 4.8m wide" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="load-title" /></div>
              <div><label className="label">Date</label><input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="load-date" /></div>
              <div><label className="label">Origin</label><input className="input" placeholder="e.g. Christchurch" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="load-origin" /></div>
              <div><label className="label">Destination</label><input className="input" placeholder="e.g. Ashburton" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="load-destination" /></div>
              <div>
                <label className="label">Direction</label>
                <select className="input" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="load-direction">
                  {DIRECTIONS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                </select>
              </div>
              <div><label className="label">Permit number</label><input className="input" placeholder="e.g. OWPV-123456" value={form.permit_no} onChange={(e) => setForm({ ...form, permit_no: e.target.value })} data-testid="load-permit" /></div>
              <div><label className="label">Pilot contact phone</label><input className="input" placeholder="+64…" value={form.pilot_phone} onChange={(e) => setForm({ ...form, pilot_phone: e.target.value })} data-testid="load-phone" /></div>
            </div>

            <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3 mb-3">
              <button type="button" onClick={autoCorridor} disabled={resolving} className="btn btn-secondary" data-testid="load-autocorridor"><Wand2 className="w-4 h-4" /> {resolving ? 'Building corridor…' : 'Auto-build corridor from origin → destination'}</button>
              {form.highway_keys.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3" data-testid="load-highways">
                  {form.highway_keys.map((k) => <span key={k} className="chip chip-outline-orange text-xs">{shortKey(k)}</span>)}
                </div>
              )}
            </div>

            {form.highway_keys.length > 0 && (
              <div className="mb-3" data-testid="load-editor-hazards">
                <HazardPanel hazards={editorHazards} context="editor" />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div><label className="label">Width (m)</label><input type="number" step="0.1" className="input" value={form.width_m} onChange={(e) => setForm({ ...form, width_m: e.target.value })} data-testid="load-width" /></div>
              <div><label className="label">Height (m)</label><input type="number" step="0.1" className="input" value={form.height_m} onChange={(e) => setForm({ ...form, height_m: e.target.value })} data-testid="load-height" /></div>
              <div><label className="label">Length (m)</label><input type="number" step="0.1" className="input" value={form.length_m} onChange={(e) => setForm({ ...form, length_m: e.target.value })} data-testid="load-length" /></div>
              <div><label className="label">Weight (t)</label><input type="number" step="0.1" className="input" value={form.weight_t} onChange={(e) => setForm({ ...form, weight_t: e.target.value })} data-testid="load-weight" /></div>
            </div>

            <label className="label">Waypoints <span className="text-zinc-400 font-normal">— click the map to drop a pin, drag to adjust</span></label>
            <p className="text-xs text-zinc-500 -mt-1 mb-2">Tap anywhere on the map to add a waypoint pin (auto-named). Freight restrictions on your corridor show as amber/red dots.</p>
            <WaypointMapEditor form={form} onAddPin={onAddPin} onMovePin={onMovePin} restrictions={editorRes} />
            <div className="space-y-2 mt-3 mb-3" data-testid="load-waypoints">
              {form.waypoints.length === 0 && <p className="text-sm text-zinc-400" data-testid="load-wp-empty">No waypoints yet — click the map above to drop one.</p>}
              {form.waypoints.map((w, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <input className="input" placeholder="Waypoint name (e.g. Rakaia bridge)" value={w.name} onChange={(e) => setWp(i, 'name', e.target.value)} data-testid={`load-wp-name-${i}`} />
                  </div>
                  <div className="w-32 shrink-0">
                    <input type="time" className="input" value={w.eta || ''} onChange={(e) => setWp(i, 'eta', e.target.value)} data-testid={`load-wp-eta-${i}`} />
                  </div>
                  {w.lat != null && <span title="Pinned on map"><MapPin className="w-4 h-4 text-teal-500 shrink-0" /></span>}
                  <button type="button" onClick={() => rmWp(i)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`load-wp-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button type="button" onClick={addWp} className="text-sm text-teal-700 font-medium inline-flex items-center gap-1" data-testid="load-add-wp"><Plus className="w-3.5 h-3.5" /> Add blank waypoint</button>
            </div>

            <div className="mb-4"><label className="label">Notes</label><textarea className="input" rows={2} placeholder="Escort details, special instructions…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="load-notes" /></div>
            <div className="flex gap-2">
              <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="load-save">{saving ? 'Saving…' : (editing === 'new' ? 'Schedule load' : 'Save changes')}</button>
              <button onClick={cancel} className="btn btn-ghost">Cancel</button>
            </div>
          </div>
        )}

        {tab !== 'completed' && <LoadsOverviewMap loads={loads} />}

        <div className="space-y-4">
          {loads.length === 0 && <div className="card text-center text-zinc-500" data-testid="loads-empty">No {tab === 'completed' ? 'completed' : 'planned'} loads {tab === 'mine' ? 'of yours ' : ''}here.</div>}
          {loads.map((l) => <LoadCard key={l.id} l={l} onEdit={startEdit} onDelete={remove} onAct={act} canCreate={canCreate} />)}
        </div>
      </div>
    </div>
  );
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 1) {
      try { map.fitBounds(L.latLngBounds(points), { padding: [30, 30] }); } catch (e) { /* noop */ }
    }
  }, [points, map]);
  return null;
}

function ClickToAdd({ onAdd }) {
  useMapEvents({ click(e) { onAdd(e.latlng.lat, e.latlng.lng); } });
  return null;
}

const wpPinIcon = (n, color = '#F7941D') => L.divIcon({
  className: 'wp-pin',
  html: `<div style="width:26px;height:26px;background:${color};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font-weight:800;font-size:12px">${n}</span></div>`,
  iconSize: [26, 26], iconAnchor: [13, 26],
});

function WaypointMapEditor({ form, onAddPin, onMovePin, restrictions }) {
  const path = form.geometry && form.geometry.length > 1
    ? form.geometry
    : (form.origin_lat != null && form.dest_lat != null ? [[form.origin_lat, form.origin_lng], [form.dest_lat, form.dest_lng]] : null);
  const pins = form.waypoints.map((w, i) => ({ w, i })).filter((x) => x.w.lat != null && x.w.lng != null);
  const bounds = [];
  if (path) path.forEach((p) => bounds.push(p));
  pins.forEach((x) => bounds.push([x.w.lat, x.w.lng]));
  const center = form.origin_lat != null ? [form.origin_lat, form.origin_lng] : (pins[0] ? [pins[0].w.lat, pins[0].w.lng] : [-41.2, 172.5]);
  return (
    <div className="rounded-lg overflow-hidden border border-zinc-200" style={{ height: 360 }} data-testid="waypoint-map-editor">
      <MapContainer center={center} zoom={7} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <ClickToAdd onAdd={onAddPin} />
        {bounds.length > 1 && <FitBounds points={bounds} />}
        {path && <Polyline positions={path} pathOptions={{ color: '#0d9488', weight: 4, opacity: 0.6, dashArray: '6 8' }} />}
        {form.origin_lat != null && <CircleMarker center={[form.origin_lat, form.origin_lng]} radius={7} pathOptions={{ color: '#0f766e', fillColor: '#2DD4B0', fillOpacity: 1 }}><Popup>Origin: {form.origin}</Popup></CircleMarker>}
        {form.dest_lat != null && <CircleMarker center={[form.dest_lat, form.dest_lng]} radius={7} pathOptions={{ color: '#b45309', fillColor: '#F7941D', fillOpacity: 1 }}><Popup>Destination: {form.destination}</Popup></CircleMarker>}
        {(restrictions || []).filter((r) => r.lat && r.lng).map((r) => (
          <CircleMarker key={`r-${r.id}`} center={[r.lat, r.lng]} radius={5}
            pathOptions={{ color: '#111827', weight: 2, fillOpacity: 0.9, fillColor: (r.condition || '').toUpperCase().startsWith('DO NOT') ? '#E11D48' : '#fbbf24' }}>
            <Popup><div className="text-xs"><div className="font-bold">{r.name}</div><div>{r.highway || r.highway_raw} · {r.condition || 'restriction'}{r.axle ? ` · axle ${r.axle}` : ''}</div></div></Popup>
          </CircleMarker>
        ))}
        {pins.map(({ w, i }) => (
          <Marker key={i} position={[w.lat, w.lng]} icon={wpPinIcon(i + 1)} draggable
            eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); onMovePin(i, ll.lat, ll.lng); } }}>
            <Popup>{w.name || `Waypoint ${i + 1}`}{w.eta ? ` · ${w.eta}` : ''}</Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

const SNAKE_COLORS = ['#F7941D', '#2DD4B0', '#E11D48', '#6366F1', '#0EA5E9', '#D946EF', '#84CC16', '#F59E0B', '#14B8A6', '#8B5CF6'];

function loadPath(l) {
  if (l.geometry && l.geometry.length > 1) return l.geometry;
  if (l.origin_lat != null && l.dest_lat != null) return [[l.origin_lat, l.origin_lng], [l.dest_lat, l.dest_lng]];
  return null;
}

function fmtDay(d) {
  try {
    const dt = new Date(d + 'T00:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tom = new Date(today); tom.setDate(tom.getDate() + 1);
    if (dt.getTime() === today.getTime()) return 'Today';
    if (dt.getTime() === tom.getTime()) return 'Tomorrow';
    return dt.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return d; }
}

function LoadsOverviewMap({ loads }) {
  const [dateFilter, setDateFilter] = useState('');
  // Build per-date summary: count + whether any load that day has a head-on meet risk.
  const byDate = {};
  for (const l of loads) {
    if (!l.date) continue;
    const hard = (l.conflicts || []).some((c) => c.opposite_direction);
    if (!byDate[l.date]) byDate[l.date] = { count: 0, conflict: false };
    byDate[l.date].count += 1;
    if (hard) byDate[l.date].conflict = true;
  }
  const dates = Object.keys(byDate).sort();
  const visible = dateFilter ? loads.filter((l) => l.date === dateFilter) : loads;
  const routes = visible.map((l, i) => ({ l, i, path: loadPath(l) })).filter((r) => r.path);
  const all = routes.flatMap((r) => r.path);
  const lat = all.length ? all.reduce((s, p) => s + p[0], 0) / all.length : -41.2;
  const lng = all.length ? all.reduce((s, p) => s + p[1], 0) / all.length : 172.5;
  return (
    <div className="card p-0 overflow-hidden mb-6" data-testid="loads-overview-map">
      <div className="px-4 py-3 border-b border-zinc-100">
        <div className="flex items-center gap-2 mb-2.5">
          <Waypoints className="w-4 h-4 text-orange-500" />
          <span className="font-display font-bold text-sm">Loads map</span>
          <span className="text-zinc-400 text-xs">· {routes.length} route{routes.length !== 1 ? 's' : ''}{dateFilter ? ` on ${fmtDay(dateFilter)}` : ''}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="snake-date-chips">
          <button onClick={() => setDateFilter('')} className={`chip ${!dateFilter ? 'chip-outline-orange' : 'chip-outline-zinc'}`} data-testid="snake-date-all">All ({loads.length})</button>
          {dates.map((d) => (
            <button key={d} onClick={() => setDateFilter(d)} className={`chip inline-flex items-center gap-1.5 ${dateFilter === d ? 'chip-outline-orange' : 'chip-outline-zinc'}`} data-testid={`snake-date-${d}`}>
              {byDate[d].conflict && <span className="w-1.5 h-1.5 rounded-full bg-red-500" title="Head-on meet risk this day" />}
              {fmtDay(d)} ({byDate[d].count})
            </button>
          ))}
        </div>
      </div>
      <div style={{ height: 400 }} className="relative">
        {routes.length === 0 && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/70 pointer-events-none">
            <span className="text-sm text-zinc-500" data-testid="snake-no-loads">No loads scheduled{dateFilter ? ` for ${fmtDay(dateFilter)}` : ''}.</span>
          </div>
        )}
        <MapContainer center={[lat, lng]} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap &copy; CARTO" />
          <FitBounds points={all} />
          {routes.map(({ l, i, path }) => {
            const color = SNAKE_COLORS[i % SNAKE_COLORS.length];
            const hardConflict = (l.conflicts || []).some((c) => c.opposite_direction);
            return (
              <div key={l.id}>
                <Polyline positions={path} pathOptions={{ color, weight: 5, opacity: 0.85 }} data-testid={`load-snake-${l.id}`}>
                  <Popup>
                    <div className="text-xs" style={{ minWidth: 170 }}>
                      <div className="font-bold" style={{ color }}>{l.title}</div>
                      <div className="mt-0.5">{l.origin} → {l.destination}</div>
                      <div className="text-zinc-500 mt-0.5">{l.date} · {l.direction}</div>
                      {l.pilot_name && <div className="text-zinc-500">Pilot: {l.pilot_name}</div>}
                      {hardConflict && <div style={{ color: '#E11D48' }} className="mt-1 font-semibold">⚠ Head-on meet risk</div>}
                    </div>
                  </Popup>
                </Polyline>
                {path.length > 0 && <CircleMarker center={path[0]} radius={5} pathOptions={{ color: '#fff', weight: 2, fillColor: color, fillOpacity: 1 }} />}
                {path.length > 1 && <CircleMarker center={path[path.length - 1]} radius={5} pathOptions={{ color: '#fff', weight: 2, fillColor: color, fillOpacity: 1 }} />}
              </div>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}

function LoadCard({ l, onEdit, onDelete, onAct, canCreate }) {
  const hard = (l.conflicts || []).filter((c) => c.opposite_direction);
  const soft = (l.conflicts || []).filter((c) => !c.opposite_direction);
  const wps = (l.waypoints || []);
  const timed = wps.filter((w) => w.eta);
  const doNotCross = (l.restrictions || []).filter((r) => (r.condition || '').toUpperCase().startsWith('DO NOT'));
  const hpmv = (l.restrictions || []).filter((r) => r.type === 'hpmv').map((r) => r.highway_key);
  const fiftyMax = (l.restrictions || []).filter((r) => r.type === '50max').map((r) => r.highway_key);
  const completed = l.status === 'completed';
  const hasMap = l.origin_lat != null && l.dest_lat != null;
  const path = loadPath(l);
  const mid = hasMap ? [(l.origin_lat + l.dest_lat) / 2, (l.origin_lng + l.dest_lng) / 2] : null;
  const [prog, setProg] = useState(new Set(l.progress || []));
  const clr = l.clearance_summary || {};
  const shareLoad = async () => {
    try {
      const { data } = await api.post(`/loads/${l.id}/share`);
      const url = `${window.location.origin}/track/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success('Tracking link copied to clipboard'); }
      catch { window.prompt('Share this tracking link:', url); }
    } catch { toast.error('Could not create tracking link'); }
  };
  const toggleReached = async (i) => {
    const reached = !prog.has(i);
    const next = new Set(prog); reached ? next.add(i) : next.delete(i); setProg(next);
    try { await api.post(`/loads/${l.id}/progress`, { waypoint_index: i, reached }); }
    catch { toast.error('Could not update progress'); setProg(new Set(prog)); }
  };

  return (
    <div className={`card ${completed ? 'opacity-80' : ''}`} data-testid={`load-card-${l.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-2">
            <span className="font-display font-bold text-lg">{l.title}</span>
            {completed ? <span className="chip chip-outline-teal text-[11px] uppercase inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Completed</span>
              : <span className="chip chip-outline-orange text-xs uppercase">{l.direction || 'other'}</span>}
            <span className="text-xs text-zinc-500">{l.date}</span>
            {l.permit_no && <span className="chip text-[11px] inline-flex items-center gap-1 bg-zinc-100 text-zinc-600"><FileText className="w-3 h-3" /> {l.permit_no}</span>}
          </div>
          <div className="text-sm text-zinc-600 mt-0.5">{l.origin} → {l.destination} · pilot <strong>{l.pilot_name}</strong>{l.pilot_company ? <span className="text-zinc-500"> · {l.pilot_company}</span> : ''}{l.pilot_phone ? <span className="inline-flex items-center gap-1 ml-1"><Phone className="w-3 h-3" />{l.pilot_phone}</span> : ''}</div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(l.highway_keys || []).map((k) => <span key={k} className="chip chip-outline-teal text-[11px]">{shortKey(k)}</span>)}
          </div>
          {(l.width_m || l.height_m || l.length_m || l.weight_t) && (
            <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500"><Ruler className="w-3.5 h-3.5" />
              {[l.width_m && `W ${l.width_m}m`, l.height_m && `H ${l.height_m}m`, l.length_m && `L ${l.length_m}m`, l.weight_t && `${l.weight_t}t`].filter(Boolean).join(' · ')}
            </div>
          )}
          {(l.clearance || []).length > 0 && (
            <div className="mt-3" data-testid={`load-clearance-${l.id}`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5" /> Clearance check
                {clr.fail > 0 && <span className="chip text-[10px] bg-red-100 text-red-700 border border-red-200">{clr.fail} blocker{clr.fail > 1 ? 's' : ''}</span>}
                {clr.warn > 0 && <span className="chip text-[10px] bg-amber-100 text-amber-800 border border-amber-200">{clr.warn} tight</span>}
                {!clr.fail && !clr.warn && <span className="chip text-[10px] bg-teal-50 text-teal-700 border border-teal-200">No blockers found</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {l.clearance.slice(0, 6).map((c, i) => (
                  <span key={i} title={c.reason}
                    className={`chip text-[11px] border ${c.level === 'fail' ? 'bg-red-100 text-red-700 border-red-200' : c.level === 'warn' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-teal-50 text-teal-700 border-teal-200'}`}>
                    {(c.name || c.highway || 'Structure')} — {c.reason}
                  </span>
                ))}
                {l.clearance.length > 6 && <span className="text-[10px] text-zinc-400 self-center">+{l.clearance.length - 6} more</span>}
              </div>
            </div>
          )}
          {(l.route_hazards || []).length > 0 && (
            <div className="mt-3" data-testid={`load-hazards-${l.id}`}>
              <HazardPanel hazards={l.route_hazards} context="card" />
            </div>
          )}
          {l.is_owner && !completed && wps.length > 0 && (
            <div className="mt-3" data-testid={`load-progress-${l.id}`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Live progress — tap a waypoint when reached</div>
              <div className="flex flex-wrap gap-1.5">
                {wps.map((w, i) => (
                  <button key={i} onClick={() => toggleReached(i)} data-testid={`load-wp-reached-${l.id}-${i}`}
                    className={`chip text-[11px] inline-flex items-center gap-1 ${prog.has(i) ? 'bg-teal-500 text-white' : 'bg-zinc-100 text-zinc-600'}`}>
                    {prog.has(i) ? <CheckCircle2 className="w-3 h-3" /> : <MapPin className="w-3 h-3" />} {i + 1}. {w.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          {canCreate && l.is_owner && !completed && <>
            <button onClick={shareLoad} title="Copy public tracking link" className="p-2 rounded-lg hover:bg-teal-50 text-teal-600" data-testid={`load-share-${l.id}`}><Share2 className="w-4 h-4" /></button>
            <button onClick={() => onAct(l.id, 'complete')} title="Mark move complete" className="p-2 rounded-lg hover:bg-teal-50 text-teal-600" data-testid={`load-complete-${l.id}`}><CheckCircle2 className="w-4 h-4" /></button>
            <button onClick={() => onEdit(l)} className="p-2 rounded-lg hover:bg-zinc-100" data-testid={`load-edit-${l.id}`}><Pencil className="w-4 h-4" /></button>
          </>}
          {canCreate && l.is_owner && completed && (
            <button onClick={() => onAct(l.id, 'reopen')} title="Reopen" className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500" data-testid={`load-reopen-${l.id}`}><RotateCcw className="w-4 h-4" /></button>
          )}
          {canCreate && l.is_owner && <button onClick={() => onDelete(l.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" data-testid={`load-delete-${l.id}`}><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>

      {/* Timeline */}
      {timed.length > 0 && (
        <div className="mt-3 overflow-x-auto" data-testid={`load-timeline-${l.id}`}>
          <div className="flex items-start gap-0 min-w-max pt-1">
            {timed.map((w, i) => (
              <div key={i} className="flex items-center">
                <div className="flex flex-col items-center px-1">
                  <div className="w-3 h-3 rounded-full bg-orange-500 ring-4 ring-orange-100" />
                  <span className="text-[11px] font-semibold mt-1">{w.eta}</span>
                  <span className="text-[11px] text-zinc-500 max-w-[90px] text-center leading-tight">{w.name}</span>
                </div>
                {i < timed.length - 1 && <div className="h-0.5 w-10 bg-orange-200 mt-1.5" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Map */}
      {hasMap && (
        <div className="mt-3 rounded-lg overflow-hidden border border-zinc-200" style={{ height: 200 }} data-testid={`load-map-${l.id}`}>
          <MapContainer center={mid} zoom={7} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false} attributionControl={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {path && path.length > 1
              ? <Polyline positions={path} pathOptions={{ color: '#F7941D', weight: 4, opacity: 0.9 }} />
              : <Polyline positions={[[l.origin_lat, l.origin_lng], [l.dest_lat, l.dest_lng]]} pathOptions={{ color: '#F7941D', weight: 3, dashArray: '6 8' }} />}
            <CircleMarker center={[l.origin_lat, l.origin_lng]} radius={7} pathOptions={{ color: '#0f766e', fillColor: '#2DD4B0', fillOpacity: 1 }}><Popup>Origin: {l.origin}</Popup></CircleMarker>
            <CircleMarker center={[l.dest_lat, l.dest_lng]} radius={7} pathOptions={{ color: '#b45309', fillColor: '#F7941D', fillOpacity: 1 }}><Popup>Destination: {l.destination}</Popup></CircleMarker>
            {(l.waypoints || []).filter((w) => w.lat != null && w.lng != null).map((w, i) => (
              <CircleMarker key={`wp-${i}`} center={[w.lat, w.lng]} radius={5} pathOptions={{ color: '#fff', weight: 2, fillColor: '#6366F1', fillOpacity: 1 }}><Popup>{i + 1}. {w.name}{w.eta ? ` · ${w.eta}` : ''}</Popup></CircleMarker>
            ))}
          </MapContainer>
        </div>
      )}

      {/* Freight restrictions overlay */}
      {(doNotCross.length > 0 || hpmv.length > 0 || fiftyMax.length > 0) && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs" data-testid={`load-restrictions-${l.id}`}>
          <ShieldAlert className="w-4 h-4 text-zinc-400" />
          {doNotCross.map((r, i) => <span key={i} className="chip chip-red text-[11px]">DO NOT CROSS: {r.name}</span>)}
          {[...new Set(hpmv)].map((k) => <span key={k} className="chip chip-outline-teal text-[11px]">{shortKey(k)} HPMV ✓</span>)}
          {[...new Set(fiftyMax)].map((k) => <span key={k} className="chip chip-outline-orange text-[11px]">{shortKey(k)} 50MAX</span>)}
        </div>
      )}

      {/* Conflicts */}
      {(hard.length > 0 || soft.length > 0) && !completed && (
        <div className="mt-3 space-y-1.5" data-testid={`load-conflicts-${l.id}`}>
          {hard.map((c) => (
            <div key={c.load_id} className="flex items-start gap-2 text-sm rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span><strong>Head-on meet risk</strong> with "{c.title}" ({c.pilot_name}{c.pilot_phone ? ` · ${c.pilot_phone}` : ''}) on {c.shared_highways.map(shortKey).join(', ')} — opposite direction{c.window[0] && c.window[1] ? `, overlapping ${c.window[0]}–${c.window[1]}` : ''}. Coordinate timing.</span>
            </div>
          ))}
          {soft.map((c) => (
            <div key={c.load_id} className="flex items-start gap-2 text-sm rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Shares {c.shared_highways.map(shortKey).join(', ')} with "{c.title}" ({c.pilot_name}){c.window[0] ? ` around ${c.window[0]}–${c.window[1]}` : ''}.</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
